import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import AppHeader from '../../components/shell/AppHeader'
import EmptyState from '../../components/shell/EmptyState'
import StatusChip, { type RecordState } from '../../components/agent/StatusChip'
import { supabase } from '../../lib/supabase'
import { useRefetchOnFocus } from '../../lib/useRefetchOnFocus'
import { getLeaveRequests, leaveRequestDisplayState, type LeaveRequest } from './leaveRequestStore'
import { getPendingDraft, type PendingDraft } from './pendingDraftStore'
import { getMyScheduleSubmissions, type MyScheduleSubmission } from './scheduleSubmissionStore'
import { useProfileIdentity } from '../../lib/useProfileIdentity'

/**
 * docs/design/journey-map.md's "Following a request": "The dashboard and
 * the request history list every submission and leave request with its
 * state." The dashboard shows a condensed view; this is the fuller one,
 * grouped by whether a request is still awaiting a decision or already has
 * one, since that grouping is what "status" means here. A leave request or
 * schedule submission awaiting a decision carries an Edit action, and a
 * returned one a Reopen and resubmit action, here, back onto AgentRequestLeavePage.tsx or AgentCopySchedulePage.tsx
 * with its own id.
 */

// docs/product/scheduling-model.md's schedule states: a draft has not
// reached a supervisor yet, so it is its own group, separate from
// "awaiting a decision" (submitted) and from an actual decision (anything
// else). A leave request is never itself in 'draft'; only a saved
// schedule can be.
const IN_PROGRESS_STATES: RecordState[] = ['draft']
const PENDING_STATES: RecordState[] = ['submitted']

type HistoryEntry = {
  key: string
  title: string
  dateRange?: string
  dayDetail?: string
  state: RecordState
  agentComment?: string
  decision?: { supervisor: string; comment: string }
  submitted?: string
  reopenHref?: string
}

function parseISO(date: string): Date {
  const [year, month, day] = date.split('-').map(Number)
  return new Date(year, month - 1, day)
}

function formatDateShort(date: string): string {
  return parseISO(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// A single day reads as just that day, not a same-to-same range.
function formatDateRange(startDate: string, endDate: string): string {
  const start = formatDateShort(startDate)
  return startDate === endDate ? start : `${start}–${formatDateShort(endDate)}`
}

// Same derivation as AgentCalendarPage.tsx's own formatRange: the draft's
// date range was never stored as its own field, only as the days it holds.
function formatDraftRange(days: PendingDraft['days']): string {
  const first = days[0]
  const last = days[days.length - 1]
  const firstLabel = `${first.month.slice(0, 3)} ${first.day}`
  const lastLabel = first.month === last.month ? `${last.day}` : `${last.month.slice(0, 3)} ${last.day}`
  return `${firstLabel}–${lastLabel}`
}

// "14:00" -> "2:00 PM": the leave form collects a 24-hour time input, but
// nothing else in this app shows one that way.
function formatTime(time: string): string {
  const [hourText, minute] = time.split(':')
  const hour = Number(hourText)
  const period = hour >= 12 ? 'PM' : 'AM'
  const hour12 = hour % 12 === 0 ? 12 : hour % 12
  return `${hour12}:${minute} ${period}`
}

function formatSubmittedDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function ArrowLeftIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  )
}

function SectionLabel({ children }: { children: string }) {
  return <h2 className="text-caption font-bold tracking-wide text-ink-subtle uppercase">{children}</h2>
}

function HistoryList({ entries, empty = '' }: { entries: HistoryEntry[]; empty?: string }) {
  if (entries.length === 0) return <EmptyState>{empty}</EmptyState>

  return (
    <ul className="mt-2 flex flex-col gap-2">
      {entries.map((entry) => (
        <li key={entry.key} className="rounded-surface border border-line p-3.5">
          <div className="flex items-center justify-between gap-3">
            <p className="text-small font-bold">{entry.title}</p>
            <StatusChip state={entry.state} />
          </div>
          {entry.dateRange && <p className="mt-1 text-small text-ink-muted">{entry.dateRange}</p>}
          {entry.dayDetail && <p className="mt-0.5 text-small text-ink-muted">{entry.dayDetail}</p>}
          {entry.agentComment && (
            <p className="mt-1.5 text-small text-ink-muted">&ldquo;{entry.agentComment}&rdquo;</p>
          )}
          {entry.decision && (
            <p className="mt-1.5 text-small text-ink-muted">
              {entry.decision.supervisor}: &ldquo;{entry.decision.comment}&rdquo;
            </p>
          )}
          {entry.submitted && <p className="mt-1.5 text-caption text-ink-subtle">Submitted {entry.submitted}</p>}
          {entry.reopenHref && (
            <Link
              to={entry.reopenHref}
              className="mt-2 flex min-h-11 w-fit items-center rounded-control text-small font-bold text-primary"
            >
              {entry.state === 'submitted' ? 'Edit' : 'Reopen and resubmit'}
            </Link>
          )}
        </li>
      ))}
    </ul>
  )
}

export default function AgentRequestHistoryPage() {
  const identity = useProfileIdentity()
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([])
  const [pendingDraft, setPendingDraft] = useState<PendingDraft | null>(null)
  // The real records, read unconditionally rather than gated on a local
  // pendingDraft existing or matching: sessionStorage does not survive a
  // new device or a cleared browser, but these do, the same reason
  // leaveRequests above never depends on anything local either. Every
  // submission the agent has ever made, not only the latest
  // (getMyScheduleSubmissions, unlike the dashboard's own
  // getMyLatestScheduleSubmission), since a history view is exactly the
  // place an earlier period's own decision should still be found.
  const [scheduleSubmissions, setScheduleSubmissions] = useState<MyScheduleSubmission[]>([])

  // Reloads the agent's own real records: called on mount, and again
  // whenever the tab regains focus (useRefetchOnFocus below), the same
  // reason AgentDashboardPage.tsx's own loadRealData does. Kicks the
  // read off rather than awaiting it directly: every setState call still
  // happens inside the `.then` callback, not this function's own body.
  function loadRealData(): void {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const id = session?.user.id
      if (id) {
        const requests = await getLeaveRequests(id)
        setLeaveRequests(requests)
        setPendingDraft(getPendingDraft(id))
        const submissions = await getMyScheduleSubmissions(id)
        setScheduleSubmissions(submissions)
      }
    })
  }

  useEffect(() => {
    loadRealData()
  }, [])

  useRefetchOnFocus(loadRealData)

  const leaveEntries: HistoryEntry[] = leaveRequests.map((request) => ({
    key: request.id,
    title: request.leaveType,
    dateRange: formatDateRange(request.startDate, request.endDate),
    dayDetail: request.fullDay
      ? 'Full day'
      : `Partial day, ${formatTime(request.startTime ?? '00:00')}–${formatTime(request.endTime ?? '00:00')}`,
    state: leaveRequestDisplayState(request.state),
    agentComment: request.comment,
    submitted: formatSubmittedDate(request.submittedAt),
    decision:
      request.decidedByName && request.decisionComment
        ? { supervisor: request.decidedByName, comment: request.decisionComment }
        : undefined,
    reopenHref: request.state === 'returned' || request.state === 'pending' ? `/agent/request-leave?reopen=${request.id}` : undefined,
  }))

  // A still-local, never-submitted draft always shows its own row:
  // pendingDraftStore.ts's own PendingDraft is the only source for that.
  // A local draft already marked 'submitted' shows only as a fallback,
  // and only while no real submission for that same period has loaded
  // yet: the real list below wins once it has, since it is what a
  // supervisor actually sees and decides, and pendingDraft.state never
  // reaches an actual decision the way a real submission's own state
  // does.
  const realSubmissionCoversDraftPeriod = scheduleSubmissions.some(
    (submission) => submission.periodStart === pendingDraft?.periodStart,
  )
  const scheduleEntries: HistoryEntry[] = [
    ...(pendingDraft && pendingDraft.state === 'draft'
      ? [
          {
            key: 'schedule-draft',
            title: `${pendingDraft.periodLabel} schedule`,
            dateRange: formatDraftRange(pendingDraft.days),
            state: 'draft' as RecordState,
          },
        ]
      : pendingDraft && pendingDraft.state === 'submitted' && !realSubmissionCoversDraftPeriod
        ? [
            {
              key: 'schedule-draft',
              title: `${pendingDraft.periodLabel} schedule`,
              dateRange: formatDraftRange(pendingDraft.days),
              state: pendingDraft.state as RecordState,
            },
          ]
        : []),
    ...scheduleSubmissions.map((submission) => ({
      key: `schedule-${submission.id}`,
      title: `${submission.periodLabel} schedule`,
      dateRange: submission.dateRange,
      state: submission.state as RecordState,
      decision:
        submission.decidedByName && submission.decisionComment
          ? { supervisor: submission.decidedByName, comment: submission.decisionComment }
          : undefined,
      reopenHref: submission.state === 'returned' || submission.state === 'submitted' ? `/agent/copy?reopen=${submission.id}` : undefined,
    })),
  ]

  const allEntries = [...leaveEntries, ...scheduleEntries]
  const inProgressEntries = allEntries.filter((entry) => IN_PROGRESS_STATES.includes(entry.state))
  const pendingEntries = allEntries.filter((entry) => PENDING_STATES.includes(entry.state))
  const decidedEntries = allEntries.filter(
    (entry) => !IN_PROGRESS_STATES.includes(entry.state) && !PENDING_STATES.includes(entry.state),
  )

  return (
    <div className="mx-auto flex h-dvh w-full max-w-md flex-col border-line bg-canvas text-ink sm:border-x lg:max-w-none lg:border-x-0">
      <a
        href="#request-history-main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-canvas focus:px-4 focus:py-2 focus:font-semibold"
      >
        Skip to main content
      </a>

      <AppHeader initials={identity?.initials ?? ''} />

      <main id="request-history-main" tabIndex={0} className="flex flex-1 flex-col overflow-y-auto">
        <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col gap-4 p-4 lg:gap-6 lg:p-8">
          <Link
            to="/agent"
            className="flex min-h-11 items-center gap-1.5 self-start rounded-control text-small font-bold text-ink-muted"
          >
            <ArrowLeftIcon />
            Dashboard
          </Link>

          <div>
            <h1 className="text-h3 font-extrabold lg:text-h2">Request History</h1>
            <p className="mt-0.5 text-small text-ink-muted">
              Every leave request and schedule submission, with its state.
            </p>
          </div>

          {inProgressEntries.length > 0 && (
            <section aria-labelledby="history-in-progress">
              <div id="history-in-progress">
                <SectionLabel>In progress</SectionLabel>
              </div>
              <HistoryList entries={inProgressEntries} />
            </section>
          )}

          <div className="flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:items-start lg:gap-6">
            <section aria-labelledby="history-pending">
              <div id="history-pending">
                <SectionLabel>Awaiting a decision</SectionLabel>
              </div>
              <HistoryList
                entries={pendingEntries}
                empty="No pending requests. Submit a leave request or a schedule change to see it here."
              />
            </section>

            <section aria-labelledby="history-decided">
              <div id="history-decided">
                <SectionLabel>Decided</SectionLabel>
              </div>
              <HistoryList
                entries={decidedEntries}
                empty="No decisions yet. Your supervisor's decisions on submitted requests appear here."
              />
            </section>
          </div>
        </div>
      </main>
    </div>
  )
}
